import React, { useCallback, useEffect, useState } from 'react';
import {
  Check,
  ClipboardCheck,
  PackageCheck,
  Plus,
  Search,
  ShoppingCart,
  X,
  XCircle
} from 'lucide-react';
import { PurchaseOrder, PurchaseOrderHeaderPayload } from '../../../../types/PurchaseOrder.types';
import { GoodsReceipt } from '../../../../types/GoodsReceipt.types';
import { Supplier } from '../../../../types/Supplier.types';
import { Society } from '../../../../types/Society.types';
import { Warehouse } from '../../../../types/Warehouse.types';
import { CostCenter } from '../../../../types/CostCenter.types';
import { Book } from '../../../../types/Book.types';
import { ModuleHeader } from '../../../../components/shared/ModuleHeader/ModuleHeader';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import Modal from '../../../../components/shared/Modal/Modal';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, RowAction, viewAction, editAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { useClickOutside, isDetailKeepZone } from '../../../../hooks/useClickOutside';
import { useToast } from '../../../../hooks/useToast';
import { useCurrentUser } from '../../../../hooks/useCurrentUser';
import { useApiUsers } from '../../../../hooks/useApiUsers';
import {
  listPurchaseOrders,
  getPurchaseOrder,
  createPurchaseOrderWithLines,
  updatePurchaseOrder,
  addPurchaseOrderLine,
  updatePurchaseOrderLine,
  removePurchaseOrderLine,
  submitPurchaseOrderForApproval,
  approvePurchaseOrder,
  rejectPurchaseOrder,
  cancelPurchaseOrder
} from '../../../../services/PurchaseOrdersService';
import { listGoodsReceipts, getGoodsReceipt, createGoodsReceiptWithLines } from '../../../../services/GoodsReceiptsService';
import { listSuppliers } from '../../../../services/SuppliersService';
import { listCompanies } from '../../../../services/CompaniesService';
import { listWarehouses } from '../../../../services/WarehousesService';
import { listCostCenters } from '../../../../services/CostCentersService';
import { listBooks } from '../../../../services/BooksService';
import { PURCHASE_ORDER_STATUS_OPTIONS, GOODS_RECEIPT_STATUS_LABELS } from '../../../../constants/purchases';
import { PurchaseOrderStatusBadge } from './components/PurchaseOrderStatusBadge';
import { PurchaseOrderForm } from './components/PurchaseOrderForm';
import { PurchaseOrderDetail, PurchaseOrderDetailSkeleton, OrderAction } from './components/PurchaseOrderDetail';
import { GoodsReceiptModal, ReceiptHeaderInput, ReceiptLineInput } from './components/GoodsReceiptModal';
import { GoodsReceiptDetail } from './components/GoodsReceiptDetail';
import { RejectReasonModal } from './components/RejectReasonModal';
import { LineDraft, lineDraftToPayload } from './components/PurchaseOrderLinesEditor';
import { formatDate, formatDateTime, formatMoney } from './components/format';
import '../shared-modules.css';
import './PurchasesView.css';

type Tab = 'orders' | 'approvals' | 'receipts';

// DataTable pagina de 50 en 50 (DataTable.tsx:153), así que todos los listados
// piden ese tamaño de página para que PREV/NEXT cuadren con el total.
const PAGE_SIZE = 50;

interface OrderFilters {
  status: string;
  supplierId: string;
  companyId: string;
  warehouseId: string;
}

const EMPTY_FILTERS: OrderFilters = { status: 'all', supplierId: '', companyId: '', warehouseId: '' };

// listBooks y listWarehouses caen a datos de maqueta si la API falla; sus ids no
// son numéricos y el backend los rechazaría por FK. Se descartan aquí.
const onlyReal = <T extends { id: string }>(items: T[]): T[] => items.filter(i => /^\d+$/.test(i.id));

export const PurchasesView = () => {
  const { notify } = useToast();
  const { currentUser } = useCurrentUser();
  const { users } = useApiUsers();

  const [tab, setTab] = useState<Tab>('orders');

  // Catálogos
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [companies, setCompanies] = useState<Society[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(true);

  // Pestaña Órdenes
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [filters, setFilters] = useState<OrderFilters>(EMPTY_FILTERS);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [orderReceipts, setOrderReceipts] = useState<GoodsReceipt[]>([]);
  const [orderReceiptsLoading, setOrderReceiptsLoading] = useState(false);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formInitial, setFormInitial] = useState<PurchaseOrder | undefined>(undefined);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [actionLoading, setActionLoading] = useState<OrderAction>(null);
  const [cancelModalOrder, setCancelModalOrder] = useState<PurchaseOrder | null>(null);
  const [rejectModalOrder, setRejectModalOrder] = useState<PurchaseOrder | null>(null);
  const [receiptModalOrder, setReceiptModalOrder] = useState<PurchaseOrder | null>(null);
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptError, setReceiptError] = useState('');

  // Pestaña Bandeja de aprobación
  const [approvals, setApprovals] = useState<PurchaseOrder[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalsTotal, setApprovalsTotal] = useState(0);
  const [approvalsPage, setApprovalsPage] = useState(1);
  const [approvalsLoaded, setApprovalsLoaded] = useState(false);

  // Pestaña Recepciones
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsTotal, setReceiptsTotal] = useState(0);
  const [receiptsPage, setReceiptsPage] = useState(1);
  const [receiptsLoaded, setReceiptsLoaded] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<GoodsReceipt | null>(null);
  const [receiptDetailLoading, setReceiptDetailLoading] = useState(false);

  const currentUserId: string | null = currentUser && currentUser.id ? String(currentUser.id) : null;

  const errorMessage = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

  // --- Catálogos --------------------------------------------------------------

  useEffect(() => {
    const loadCatalogs = async () => {
      setCatalogsLoading(true);
      const results = await Promise.all([
        listSuppliers().catch(err => { console.error('Error fetching suppliers:', err); return null; }),
        listCompanies().catch(err => { console.error('Error fetching companies:', err); return null; }),
        listWarehouses().catch(err => { console.error('Error fetching warehouses:', err); return null; }),
        listCostCenters().catch(err => { console.error('Error fetching cost centers:', err); return null; }),
        listBooks().catch(err => { console.error('Error fetching books:', err); return null; })
      ]);
      const [sup, comp, wh, cc, bk] = results;
      setSuppliers(sup || []);
      setCompanies(comp || []);
      setWarehouses(onlyReal(wh || []));
      setCostCenters(cc || []);
      setBooks(onlyReal(bk || []));
      if (results.some(r => r === null)) {
        notify('Aviso', 'No se pudo cargar alguno de los catálogos (acreedores, sociedades, almacenes, centros de coste o libros).', 'warning');
      }
      setCatalogsLoading(false);
    };
    loadCatalogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Órdenes ---------------------------------------------------------------

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const page = await listPurchaseOrders({
        status:      filters.status === 'all' ? undefined : filters.status,
        supplierId:  filters.supplierId || undefined,
        companyId:   filters.companyId || undefined,
        warehouseId: filters.warehouseId || undefined,
        page:        ordersPage,
        pageSize:    PAGE_SIZE
      });
      setOrders(page.data);
      setOrdersTotal(page.total);
      setOrdersError('');
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
      setOrders([]);
      setOrdersTotal(0);
      setOrdersError(errorMessage(err, 'No se pudo cargar el listado de órdenes de compra.'));
    } finally {
      setOrdersLoading(false);
    }
  }, [filters, ordersPage]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Filtrar o buscar cambia el listado: la ficha abierta se cierra para que la
  // tabla vuelva a ocupar todo el ancho y no quede una orden que ya no se lista.
  const updateFilters = (patch: Partial<OrderFilters>) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setOrdersPage(1);
    setSelectedOrderId(null);
  };

  const updateSearch = (value: string) => {
    setSearchQuery(value);
    setSelectedOrderId(null);
  };

  // La ficha se pide completa (líneas + historial) y sus albaranes en paralelo.
  const loadOrderDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setOrderReceiptsLoading(true);
    try {
      const [order, receiptsPage] = await Promise.all([
        getPurchaseOrder(id),
        listGoodsReceipts({ purchaseOrderId: id, pageSize: 100 })
          .catch(err => { console.error('Error fetching order receipts:', err); return null; })
      ]);
      setSelectedOrder(order);
      setOrderReceipts(receiptsPage ? receiptsPage.data : []);
    } catch (err) {
      console.error('Error fetching purchase order:', err);
      setSelectedOrder(null);
      setOrderReceipts([]);
      notify('Error', errorMessage(err, 'No se pudo cargar la orden de compra.'), 'urgent');
    } finally {
      setDetailLoading(false);
      setOrderReceiptsLoading(false);
    }
    // notify no es estable entre renders; incluirlo relanzaría el efecto en bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedOrderId) loadOrderDetail(selectedOrderId);
    else { setSelectedOrder(null); setOrderReceipts([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderId]);

  const refreshSelected = async () => {
    if (selectedOrderId) await loadOrderDetail(selectedOrderId);
  };

  // --- Bandeja de aprobación -------------------------------------------------

  // approver_id replica en servidor la regla de approve.avap (supervisor del
  // solicitante o, si no tiene, el supervisor por defecto).
  const fetchApprovals = useCallback(async () => {
    if (!currentUserId) return;
    setApprovalsLoading(true);
    try {
      const page = await listPurchaseOrders({
        status:     'pending_approval',
        approverId: currentUserId,
        page:       approvalsPage,
        pageSize:   PAGE_SIZE
      });
      setApprovals(page.data);
      setApprovalsTotal(page.total);
      setApprovalsLoaded(true);
    } catch (err) {
      console.error('Error fetching approvals:', err);
      setApprovals([]);
      setApprovalsTotal(0);
      notify('Error', errorMessage(err, 'No se pudo cargar la bandeja de aprobación.'), 'urgent');
    } finally {
      setApprovalsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, approvalsPage]);

  // Se carga en cuanto se conoce el usuario (para el contador de la pestaña) y
  // se recarga al cambiar de página.
  useEffect(() => { fetchApprovals(); }, [fetchApprovals]);

  // --- Recepciones -----------------------------------------------------------

  const fetchReceipts = useCallback(async () => {
    setReceiptsLoading(true);
    try {
      const page = await listGoodsReceipts({ page: receiptsPage, pageSize: PAGE_SIZE });
      setReceipts(page.data);
      setReceiptsTotal(page.total);
      setReceiptsLoaded(true);
    } catch (err) {
      console.error('Error fetching goods receipts:', err);
      setReceipts([]);
      setReceiptsTotal(0);
      notify('Error', errorMessage(err, 'No se pudo cargar el listado de recepciones.'), 'urgent');
    } finally {
      setReceiptsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptsPage]);

  useEffect(() => {
    if (tab === 'receipts') fetchReceipts();
  }, [tab, fetchReceipts]);

  const handleSelectReceipt = async (receipt: GoodsReceipt) => {
    setReceiptDetailLoading(true);
    try {
      const full = await getGoodsReceipt(receipt.id);
      // goods_receipts_get no trae po_number; se conserva el del listado.
      setSelectedReceipt({ ...full, poNumber: full.poNumber || receipt.poNumber });
    } catch (err) {
      console.error('Error fetching goods receipt:', err);
      setSelectedReceipt(null);
      notify('Error', errorMessage(err, 'No se pudo cargar el albarán.'), 'urgent');
    } finally {
      setReceiptDetailLoading(false);
    }
  };

  // Al cambiar de pestaña no se arrastra la ficha abierta en la anterior:
  // cada pestaña empieza con la tabla a ancho completo.
  const switchTab = (next: Tab) => {
    if (next === tab) return;
    setSelectedOrderId(null);
    setSelectedReceipt(null);
    setIsFormOpen(false);
    setTab(next);
  };

  const handleOpenOrderFromReceipt = (purchaseOrderId: string) => {
    switchTab('orders');
    setSelectedOrderId(purchaseOrderId);
  };

  // --- Derivados -------------------------------------------------------------

  const q = searchQuery.trim().toLowerCase();
  const filteredOrders = q
    ? orders.filter(o => o.poNumber.toLowerCase().includes(q) || o.supplierName.toLowerCase().includes(q))
    : orders;

  // Solo UX: aunque se muestre el botón, approve.avap devuelve 403 si no coincide.
  const canApprove = (order: PurchaseOrder): boolean => {
    if (!currentUserId) return false;
    const requester = users.find(u => u.id === order.createdBy);
    const expected = (requester && requester.supervisorId) || (users.find(u => u.isDefaultSupervisor) || { id: null }).id;
    return expected === currentUserId;
  };

  const userName = (id: string | null): string => {
    if (!id) return '';
    const u = users.find(x => x.id === id);
    return u ? u.name : '';
  };

  const warehouseName = (id: string): string => {
    const w = warehouses.find(x => x.id === id);
    return w ? w.name : '';
  };

  // --- Panel lateral ---------------------------------------------------------

  const selectOrder = (id: string) => {
    setIsFormOpen(false);
    setSelectedOrderId(id);
  };

  const closePanel = () => {
    setSelectedOrderId(null);
    setSelectedReceipt(null);
  };

  // Clic fuera cierra la ficha de orden o de albarán. El formulario de OC no:
  // esta vista no avisa de cambios sin guardar y se perdería lo escrito; se
  // cierra con su X. Los modales (cancelar, rechazar, recepción) van en portal.
  useClickOutside(isDetailKeepZone, closePanel, Boolean(selectedOrderId || selectedReceipt) && !isFormOpen);

  // --- Alta / edición --------------------------------------------------------

  const handleCreateNewClick = () => {
    setSelectedOrderId(null);
    setFormMode('create');
    setFormInitial(undefined);
    setFormError('');
    setIsFormOpen(true);
  };

  const handleEditClick = (order: PurchaseOrder) => {
    if (order.status !== 'draft') {
      notify('Operación no permitida', 'Solo se pueden editar órdenes en borrador.', 'warning');
      return;
    }
    setFormMode('edit');
    setFormInitial(order);
    setFormError('');
    setIsFormOpen(true);
  };

  // Edición de borrador: cabecera por update y líneas por diff contra las
  // persistidas (sin id → add; con id y cambios → update; ausentes → remove).
  const saveDraftEdits = async (order: PurchaseOrder, header: PurchaseOrderHeaderPayload, lines: LineDraft[]) => {
    await updatePurchaseOrder(order.id, header);

    const failures: string[] = [];
    const keptIds = new Set(lines.filter(l => l.id).map(l => l.id as string));

    for (const original of order.lines) {
      if (!keptIds.has(original.id)) {
        try { await removePurchaseOrderLine(original.id); }
        catch (err) { failures.push(`eliminar "${original.description}": ${errorMessage(err, 'error')}`); }
      }
    }

    for (const draft of lines) {
      const payload = lineDraftToPayload(draft);
      try {
        if (!draft.id) {
          await addPurchaseOrderLine(order.id, payload);
        } else {
          const original = order.lines.find(l => l.id === draft.id);
          const changed = !original
            || original.description !== payload.description
            || original.bookId !== (payload.bookId || null)
            || original.sku !== (payload.sku || '')
            || original.supplierReference !== (payload.supplierReference || '')
            || original.quantityOrdered !== payload.quantityOrdered
            || original.unitOfMeasure !== payload.unitOfMeasure
            || original.unitPrice !== payload.unitPrice
            || original.discountPct !== payload.discountPct
            || original.taxRate !== payload.taxRate;
          if (changed) await updatePurchaseOrderLine(draft.id, payload);
        }
      } catch (err) {
        failures.push(`"${payload.description}": ${errorMessage(err, 'error')}`);
      }
    }
    return failures;
  };

  const handleSaveOrder = async (header: PurchaseOrderHeaderPayload, lines: LineDraft[]) => {
    setIsSaving(true);
    setFormError('');
    try {
      if (formMode === 'edit' && formInitial) {
        const failures = await saveDraftEdits(formInitial, header, lines);
        if (failures.length > 0) {
          notify('Aviso', `Orden modificada, pero ${failures.length} línea(s) no se guardaron: ${failures.join('; ')}`, 'warning');
        } else {
          notify('Éxito', 'Orden de compra modificada exitosamente', 'info');
        }
        setIsFormOpen(false);
        await Promise.all([fetchOrders(), refreshSelected()]);
        return;
      }

      const { order, failedLines } = await createPurchaseOrderWithLines(header, lines.map(lineDraftToPayload));
      if (failedLines.length > 0) {
        // La OC existe en borrador con las líneas que sí entraron; las que no,
        // se vuelven a añadir desde "Editar". Se avisa de cuáles y por qué.
        notify(
          'Aviso',
          `Orden ${order.poNumber} creada como borrador, pero ${failedLines.length} línea(s) no se guardaron: ${failedLines.map(f => `línea ${f.index + 1}: ${f.error}`).join('; ')}`,
          'warning'
        );
      } else {
        notify('Éxito', `Orden de compra ${order.poNumber} creada como borrador`, 'info');
      }
      setIsFormOpen(false);
      setSelectedOrderId(order.id);
      await fetchOrders();
    } catch (err) {
      console.error('Error saving purchase order:', err);
      setFormError(errorMessage(err, 'No se pudo guardar la orden de compra.'));
    } finally {
      setIsSaving(false);
    }
  };

  // --- Acciones de ciclo de vida ----------------------------------------------

  const runOrderAction = async (
    action: Exclude<OrderAction, null>,
    fn: () => Promise<PurchaseOrder>,
    successMessage: (order: PurchaseOrder) => string,
    notPermittedPattern?: RegExp
  ) => {
    setActionLoading(action);
    try {
      const updated = await fn();
      notify('Éxito', successMessage(updated), 'info');
      await Promise.all([fetchOrders(), fetchApprovals(), refreshSelected()]);
      return true;
    } catch (err) {
      console.error(`Error on purchase order action ${action}:`, err);
      const msg = errorMessage(err, 'No se pudo completar la operación.');
      if (notPermittedPattern && notPermittedPattern.test(msg)) notify('Operación no permitida', msg, 'warning');
      else notify('Error', msg, 'urgent');
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubmitForApproval = (order: PurchaseOrder) =>
    runOrderAction('submit', () => submitPurchaseOrderForApproval(order.id), o => `Orden ${o.poNumber} enviada a aprobación`, /lineas|draft/i);

  const handleApprove = (order: PurchaseOrder) =>
    runOrderAction('approve', () => approvePurchaseOrder(order.id), o => `Orden ${o.poNumber} aprobada`, /supervisor|pending_approval/i);

  const handleRejectConfirm = async (reason: string) => {
    if (!rejectModalOrder) return;
    const order = rejectModalOrder;
    const ok = await runOrderAction('reject', () => rejectPurchaseOrder(order.id, reason), o => `Orden ${o.poNumber} devuelta a borrador`, /supervisor|pending_approval/i);
    if (ok) setRejectModalOrder(null);
  };

  const handleCancelConfirm = async () => {
    if (!cancelModalOrder) return;
    const order = cancelModalOrder;
    setCancelModalOrder(null);
    await runOrderAction('cancel', () => cancelPurchaseOrder(order.id), o => `Orden ${o.poNumber} cancelada`, /recibida|cancelada/i);
  };

  // --- Recepción -------------------------------------------------------------

  const handleReceiptConfirm = async (header: ReceiptHeaderInput, lines: ReceiptLineInput[]) => {
    if (!receiptModalOrder) return;
    const order = receiptModalOrder;
    setReceiptSaving(true);
    setReceiptError('');
    try {
      const { receipt, purchaseOrderStatus, failedLines } = await createGoodsReceiptWithLines(
        {
          purchaseOrderId:    order.id,
          warehouseId:        order.warehouseId,
          deliveryNoteNumber: header.deliveryNoteNumber || undefined,
          packageCount:       header.packageCount,
          notes:              header.notes || undefined
        },
        lines
      );

      if (failedLines.length > 0) {
        const lineName = (id: string) => (order.lines.find(l => l.id === id) || { description: id }).description;
        notify(
          'Aviso',
          `Albarán ${receipt.deliveryNoteNumber || `#${receipt.id}`} registrado, pero ${failedLines.length} línea(s) no entraron: ${failedLines.map(f => `"${lineName(f.purchaseOrderLineId)}": ${f.error}`).join('; ')}`,
          'warning'
        );
      } else if (purchaseOrderStatus === 'received') {
        notify('Éxito', `Recepción registrada. La orden ${order.poNumber} queda completamente recibida.`, 'info');
      } else {
        notify('Éxito', `Recepción parcial registrada en ${order.poNumber}. Quedan unidades pendientes.`, 'info');
      }

      setReceiptModalOrder(null);
      await Promise.all([fetchOrders(), refreshSelected(), receiptsLoaded ? fetchReceipts() : Promise.resolve()]);
    } catch (err) {
      console.error('Error registering goods receipt:', err);
      setReceiptError(errorMessage(err, 'No se pudo registrar la recepción.'));
    } finally {
      setReceiptSaving(false);
    }
  };

  // --- Columnas --------------------------------------------------------------

  const orderColumns: ColumnDef<PurchaseOrder>[] = [
    {
      id: 'poNumber',
      header: 'ORDEN',
      render: (o) => (
        <>
          <div className="purchases-view__po-number">{o.poNumber}</div>
          <div className="purchases-view__po-sub">{formatDate(o.createdAt)}</div>
        </>
      )
    },
    {
      id: 'supplierName',
      header: 'ACREEDOR / SOCIEDAD',
      render: (o) => (
        <>
          <div className="purchases-view__po-supplier">{o.supplierName || `#${o.supplierId}`}</div>
          <div className="purchases-view__po-sub">{o.companyName || `#${o.companyId}`}</div>
        </>
      )
    },
    { id: 'warehouseName', header: 'ALMACÉN', render: (o) => o.warehouseName || `#${o.warehouseId}` },
    { id: 'expectedDeliveryDate', header: 'ENTREGA', render: (o) => formatDate(o.expectedDeliveryDate) },
    { id: 'totalAmount', header: 'TOTAL', align: 'right', render: (o) => <span className="purchases-view__po-total">{formatMoney(o.totalAmount, o.currency)}</span> },
    { id: 'status', header: 'ESTADO', align: 'center', render: (o) => <PurchaseOrderStatusBadge status={o.status} /> }
  ];

  const approvalColumns: ColumnDef<PurchaseOrder>[] = [
    { id: 'poNumber', header: 'ORDEN', render: (o) => <span className="purchases-view__po-number">{o.poNumber}</span> },
    { id: 'createdByName', header: 'SOLICITANTE', render: (o) => o.createdByName || userName(o.createdBy) || '—' },
    { id: 'supplierName', header: 'ACREEDOR', render: (o) => o.supplierName || `#${o.supplierId}` },
    { id: 'updatedAt', header: 'ENVIADA', render: (o) => formatDateTime(o.updatedAt || o.createdAt) },
    { id: 'totalAmount', header: 'TOTAL', align: 'right', render: (o) => <span className="purchases-view__po-total">{formatMoney(o.totalAmount, o.currency)}</span> }
  ];

  const receiptColumns: ColumnDef<GoodsReceipt>[] = [
    {
      id: 'deliveryNoteNumber',
      header: 'ALBARÁN',
      render: (r) => (
        <>
          <div className="purchases-view__po-number">{r.deliveryNoteNumber || `#${r.id}`}</div>
          <div className="purchases-view__po-sub">{r.poNumber}</div>
        </>
      )
    },
    { id: 'receivedAt', header: 'FECHA', render: (r) => formatDateTime(r.receivedAt) },
    { id: 'warehouseId', header: 'ALMACÉN', render: (r) => warehouseName(r.warehouseId) || `#${r.warehouseId}` },
    { id: 'receivedBy', header: 'RECIBIDO POR', render: (r) => userName(r.receivedBy) || (r.receivedBy ? `#${r.receivedBy}` : '—') },
    {
      id: 'status',
      header: 'ESTADO',
      align: 'center',
      render: (r) => (
        <span className={`module-badge ${r.status === 'complete' ? 'completed' : 'pending'}`}>
          {GOODS_RECEIPT_STATUS_LABELS[r.status]}
        </span>
      )
    }
  ];

  const orderRowActions = (o: PurchaseOrder): RowAction[] => {
    const actions: RowAction[] = [viewAction(() => selectOrder(o.id))];
    if (o.status === 'draft') actions.push(editAction(() => { setSelectedOrderId(o.id); handleEditClick(o); }));
    if (o.status !== 'received' && o.status !== 'cancelled') {
      actions.push({ label: 'Cancelar orden', icon: XCircle, tone: 'danger', onSelect: () => setCancelModalOrder(o) });
    }
    return actions;
  };

  const approvalRowActions = (o: PurchaseOrder): RowAction[] => [
    viewAction(() => selectOrder(o.id)),
    { label: 'Aprobar', icon: Check, tone: 'accent', disabled: actionLoading !== null, onSelect: () => handleApprove(o) },
    { label: 'Rechazar', icon: X, tone: 'danger', disabled: actionLoading !== null, onSelect: () => setRejectModalOrder(o) }
  ];

  // --- Render ----------------------------------------------------------------

  const renderOrderPanel = (canApproveOverride?: boolean) => {
    if (!selectedOrderId) return null;
    if (detailLoading || !selectedOrder) return <PurchaseOrderDetailSkeleton onClose={() => setSelectedOrderId(null)} />;
    return (
      <PurchaseOrderDetail
        order={selectedOrder}
        receipts={orderReceipts}
        receiptsLoading={orderReceiptsLoading}
        canApprove={canApproveOverride !== undefined ? canApproveOverride : canApprove(selectedOrder)}
        actionLoading={actionLoading}
        onEdit={() => handleEditClick(selectedOrder)}
        onSubmitForApproval={() => handleSubmitForApproval(selectedOrder)}
        onApprove={() => handleApprove(selectedOrder)}
        onReject={() => setRejectModalOrder(selectedOrder)}
        onRegisterReceipt={() => { setReceiptError(''); setReceiptModalOrder(selectedOrder); }}
        onCancel={() => setCancelModalOrder(selectedOrder)}
        onSelectReceipt={(r) => { switchTab('receipts'); handleSelectReceipt(r); }}
        onClose={() => setSelectedOrderId(null)}
      />
    );
  };

  return (
    <>
      {/* Un solo layout para las tres pestañas: título, pestañas y tabla a la
          izquierda; en el drawer, el panel de la pestaña activa. */}
      <MasterDetailLayout
        isOpen={tab === 'orders' ? Boolean(selectedOrderId || isFormOpen)
          : tab === 'approvals' ? Boolean(selectedOrderId)
          : Boolean(selectedReceipt || receiptDetailLoading)}
        wide={tab === 'orders' && isFormOpen}
        drawer
      >
        <div className="purchases-view">
          <div className="module-header-row">
            <ModuleHeader
              title="Gestión_De_Compras"
              subtitle="Órdenes de compra a acreedores, aprobación por el supervisor y recepción de mercancía en almacén."
            />
            {tab === 'orders' && (
              <button onClick={handleCreateNewClick} disabled={catalogsLoading} className="module-create-btn" data-keep-detail>
                <Plus size={14} /> Nueva_Orden
              </button>
            )}
          </div>

          <div className="purchases-view__tabs">
            <button type="button" onClick={() => switchTab('orders')} className={`purchases-view__tab ${tab === 'orders' ? 'active' : ''}`}>
              <ShoppingCart size={12} /> Órdenes_De_Compra
            </button>
            <button type="button" onClick={() => switchTab('approvals')} className={`purchases-view__tab ${tab === 'approvals' ? 'active' : ''}`}>
              <ClipboardCheck size={12} /> Bandeja_De_Aprobación
              {approvalsTotal > 0 && <span className="purchases-view__tab-count">{approvalsTotal}</span>}
            </button>
            <button type="button" onClick={() => switchTab('receipts')} className={`purchases-view__tab ${tab === 'receipts' ? 'active' : ''}`}>
              <PackageCheck size={12} /> Recepciones
            </button>
          </div>

          {/* ---------------- Órdenes ---------------- */}
          {tab === 'orders' && (
            <>
              <div className="module-toolbar">
                <div className="module-search-wrapper">
                  <Search className="module-search-icon" size={14} />
                  <input
                    type="text"
                    placeholder="FILTRAR ESTA PÁGINA POR Nº DE ORDEN O ACREEDOR..."
                    value={searchQuery}
                    onChange={(e) => updateSearch(e.target.value)}
                    className="module-search-input"
                  />
                </div>
                <select
                  value={filters.status}
                  onChange={(e) => updateFilters({ status: e.target.value })}
                  className="module-filter-select"
                >
                  {PURCHASE_ORDER_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select
                  value={filters.supplierId}
                  onChange={(e) => updateFilters({ supplierId: e.target.value })}
                  className="module-filter-select"
                >
                  <option value="">TODOS LOS ACREEDORES</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select
                  value={filters.companyId}
                  onChange={(e) => updateFilters({ companyId: e.target.value })}
                  className="module-filter-select"
                >
                  <option value="">TODAS LAS SOCIEDADES</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select
                  value={filters.warehouseId}
                  onChange={(e) => updateFilters({ warehouseId: e.target.value })}
                  className="module-filter-select"
                >
                  <option value="">TODOS LOS ALMACENES</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                {(filters.status !== 'all' || filters.supplierId || filters.companyId || filters.warehouseId) && (
                  <button type="button" onClick={() => updateFilters(EMPTY_FILTERS)} className="module-action-btn" title="Quitar filtros">
                    <X size={12} />
                  </button>
                )}
              </div>

              {ordersError ? (
                <div className="module-panel">
                  <div className="module-panel-empty">{ordersError}</div>
                </div>
              ) : (
                <DataTable
                  data={filteredOrders}
                  columns={orderColumns}
                  title={<><ShoppingCart size={14} className="module-panel-title-icon" /> Órdenes_De_Compra</>}
                  subtitle={ordersLoading ? undefined : `TOTAL: ${ordersTotal}`}
                  isLoading={ordersLoading}
                  showPagination
                  currentPage={ordersPage}
                  totalRecords={ordersTotal}
                  onPageChange={setOrdersPage}
                  getRowId={(o) => o.id}
                  onRowClick={(o) => selectOrder(o.id)}
                  selectedRowId={selectedOrderId}
                  emptyText="No hay órdenes de compra con estos filtros."
                  actionsHeader="ACCIONES"
                  actionsColumn={(o) => <RowActionsMenu actions={orderRowActions(o)} />}
                />
              )}
            </>
          )}

          {/* ---------------- Bandeja de aprobación ---------------- */}
          {tab === 'approvals' && (
            <DataTable
              data={approvals}
              columns={approvalColumns}
              title={<><ClipboardCheck size={14} className="module-panel-title-icon" /> Pendientes_De_Mi_Firma</>}
              subtitle={approvalsLoading || !approvalsLoaded ? undefined : `TOTAL: ${approvalsTotal}`}
              isLoading={approvalsLoading || !currentUserId}
              showPagination
              currentPage={approvalsPage}
              totalRecords={approvalsTotal}
              onPageChange={setApprovalsPage}
              getRowId={(o) => o.id}
              onRowClick={(o) => selectOrder(o.id)}
              selectedRowId={selectedOrderId}
              emptyText="No tiene órdenes de compra pendientes de aprobar."
              actionsHeader="ACCIONES"
              actionsColumn={(o) => <RowActionsMenu actions={approvalRowActions(o)} />}
            />
          )}

          {/* ---------------- Recepciones ---------------- */}
          {tab === 'receipts' && (
            <DataTable
              data={receipts}
              columns={receiptColumns}
              title={<><PackageCheck size={14} className="module-panel-title-icon" /> Albaranes_De_Entrada</>}
              subtitle={receiptsLoading ? undefined : `TOTAL: ${receiptsTotal}`}
              isLoading={receiptsLoading}
              showPagination
              currentPage={receiptsPage}
              totalRecords={receiptsTotal}
              onPageChange={setReceiptsPage}
              getRowId={(r) => r.id}
              onRowClick={handleSelectReceipt}
              selectedRowId={selectedReceipt ? selectedReceipt.id : null}
              emptyText="Todavía no se ha registrado ninguna recepción de mercancía."
            />
          )}
        </div>

        {tab === 'orders' && (isFormOpen ? (
          <PurchaseOrderForm
            key={`${formMode}-${formInitial ? formInitial.id : 'new'}`}
            mode={formMode}
            initial={formInitial}
            suppliers={suppliers}
            companies={companies}
            warehouses={warehouses}
            costCenters={costCenters}
            books={books}
            isSaving={isSaving}
            error={formError}
            onSubmit={handleSaveOrder}
            onCancel={() => { setIsFormOpen(false); setSelectedOrderId(null); }}
            onValidationError={(msg) => { setFormError(msg); notify('Aviso', msg, 'warning'); }}
          />
        ) : (
          renderOrderPanel()
        ))}
        {tab === 'approvals' && renderOrderPanel(true)}
        {tab === 'receipts' && (selectedReceipt || receiptDetailLoading) && (
          <GoodsReceiptDetail
            receipt={selectedReceipt}
            loading={receiptDetailLoading}
            warehouseName={selectedReceipt ? warehouseName(selectedReceipt.warehouseId) : ''}
            receivedByName={selectedReceipt ? userName(selectedReceipt.receivedBy) : ''}
            onOpenOrder={handleOpenOrderFromReceipt}
            onClose={() => setSelectedReceipt(null)}
          />
        )}
      </MasterDetailLayout>

      {/* ---------------- Modales ---------------- */}
      <Modal
        isOpen={!!cancelModalOrder}
        type="urgent"
        title="Cancelar orden de compra"
        message={`¿Seguro que desea cancelar la orden ${cancelModalOrder ? cancelModalOrder.poNumber : ''}? No se podrá reactivar ni recibir mercancía contra ella.`}
        confirmText="Cancelar orden"
        cancelText="Volver"
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelModalOrder(null)}
      />

      <RejectReasonModal
        order={rejectModalOrder}
        isSaving={actionLoading === 'reject'}
        onConfirm={handleRejectConfirm}
        onCancel={() => setRejectModalOrder(null)}
      />

      <GoodsReceiptModal
        order={receiptModalOrder}
        isSaving={receiptSaving}
        error={receiptError}
        onConfirm={handleReceiptConfirm}
        onCancel={() => { if (!receiptSaving) setReceiptModalOrder(null); }}
      />
    </>
  );
};
